<script>
/**
 * Próximo passo do cliente, logo abaixo do switcher da sidebar.
 *
 * A sidebar de cliente acompanha o profissional nas telas do cliente —
 * carteira, CRM, compliance. É o único lugar onde "o que falta neste cliente"
 * fica visível sem ele voltar para a listagem ou para o resumo.
 *
 * Some quando não há jornada, quando não há passo em aberto ou com o rail
 * recolhido: um card de texto num rail de 84px vira ruído ilegível.
 */
import { executarAcaoDoPasso } from '@modules/client-journey/utils/executar-acao-do-passo/executar-acao-do-passo'
import { isClientJourneyEnabled } from '@modules/client-journey/utils/journey-enabled/journey-enabled'

export default {
  name: 'JourneySidebarNext',
  props: {
    collapsed: { type: Boolean, default: false },
  },
  computed: {
    habilitado() {
      return isClientJourneyEnabled(this.$access)
    },
    client() {
      return this.$currentClient() || {}
    },
    clientId() {
      return this.client.id || null
    },
    journey() {
      return this.$store.getters['clientJourney/journeyDoCliente'](this.clientId)
    },
    proximo() {
      const acao = this.journey && this.journey.nextAction
      return acao && acao.label ? acao : null
    },
    visivel() {
      return this.habilitado && !this.collapsed && !!this.proximo
    },
  },
  watch: {
    clientId: {
      immediate: true,
      handler() {
        this.carregar()
      },
    },
  },
  methods: {
    carregar() {
      if (!this.habilitado || !this.clientId) return
      // A jornada fica em cache por cliente no store: trocar de tela dentro do
      // mesmo cliente não refaz a chamada.
      if (this.journey) return
      this.$store.dispatch('clientJourney/carregarTemplate')
      this.$store.dispatch('clientJourney/carregarCliente', this.clientId)
    },
    executar() {
      executarAcaoDoPasso({
        actionKey: this.proximo.actionKey,
        stepLabel: this.proximo.label,
        stageLabel: this.proximo.stageLabel,
        client: this.client,
        router: this.$router,
        showModal: (modal) => this.$showModal(modal),
        onIndisponivel: (motivo) =>
          this.$showSnack({ status: 400, message: motivo }),
      })
    },
  },
}
</script>

<template>
  <button v-if="visivel" type="button" class="jsn" @click="executar">
    <span class="jsn__rotulo">Próximo passo</span>
    <span class="jsn__passo">{{ proximo.label }}</span>
    <span v-if="proximo.stageLabel" class="jsn__etapa">
      {{ proximo.stageLabel }}
    </span>
  </button>
</template>

<style lang="sass" scoped>
// A sidebar é escura: as cores saem de --nav-fg-rgb, a mesma variável que o
// switcher logo acima usa. Token fixo aqui apagaria o card no white-label de
// navbar clara.
.jsn
  display: flex
  flex-direction: column
  align-items: flex-start
  gap: 2px
  width: calc(100% - 32px)
  margin: 12px 16px
  padding: 10px 12px
  border: 1px solid rgba(var(--nav-fg-rgb, 255, 255, 255), 0.12)
  border-radius: var(--radius-md)
  background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.06)
  text-align: left
  cursor: pointer
  transition: background-color var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover
    background: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.12)

  &__rotulo
    font-size: 10px
    font-weight: 600
    letter-spacing: .06em
    text-transform: uppercase
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.55)

  &__passo
    font-size: 13px
    font-weight: 600
    line-height: 1.3
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.95)

  &__etapa
    font-size: 11px
    color: rgba(var(--nav-fg-rgb, 255, 255, 255), 0.55)
</style>
